/*
 * Description:
 * ---------------------
 * Software Updates: a box installs Weavian's new releases when someone updates
 * it from here, or by itself in its site's update hour once its customer turns
 * automatic updates on; it checks the new version works and goes back to the
 * one before by itself if it does not (cloud-anchor/pkg/release,
 * server/pkg/releases). This screen says where each site stands, lets a
 * customer's administrators choose the hour or turn automatic updates on,
 * update a site now and restart one whose system updates wait for it, and
 * shows the releases, which super admins can pause. Every refusal is the
 * server's own sentence; all other wording is in updateWords.js.
 *
 * ! file components/UpdatesPanel.js
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useConfirm } from "./ConfirmModal";
import { useSession } from "./PortalGate";
import * as W from "./updateWords";
import "./Firewall.css";
import "./Updates.css";

const POLL_MS = 15000;
const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);

/*
 * useUpdates reads /updates while the page shows: the server's view, why it
 * could not be read in the screen's words, and read to read it again at once.
 */
function useUpdates(serverApiUrl) {
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const read = useCallback(async () => {
    const res = await fetchWithTimeout(serverApiUrl("/updates"), { credentials: "same-origin" }).catch(() => null);
    if (!mounted.current) return;
    if (!res) return setError("The Weavian server did not answer.");
    if (res.status === 404) return setError(W.NOT_ON_SERVER);
    const out = await res.json().catch(() => ({}));
    if (!res.ok) return setError(out.error || `The server refused (HTTP ${res.status}).`);
    setError("");
    setView(out);
  }, [serverApiUrl]);
  useVisibleInterval(read, POLL_MS);
  return { view, error, setError, read, mounted };
}

/*
 * SiteSoftware is one site's software in a line, for its own dashboard: the
 * release it runs and whether it is up to date, in the words of its row on
 * Software Updates, which onOpen opens when there is something to do there.
 * Nothing for a site the server does not list (a phone, a gateway) or when it
 * offers no updates.
 */
export function SiteSoftware({ serverApiUrl, node, onOpen }) {
  const canAct = !!useSession().isTenantAdmin;
  const { view } = useUpdates(serverApiUrl);
  const site = view?.sites?.find((s) => s.node === node);
  if (!site) return null;
  const n = W.next(site, canAct ? "in Software Updates" : "");
  return (
    <p className="dash-page-sub dash-software">
      <span className={`dash-software-dot is-${n.tone || "none"}`} aria-hidden="true" />
      <span>
        <strong>Software:</strong> {W.runs(site)}. {n.text}
        {onOpen && ((canAct && site.available > 0) || n.tone === "bad") && (
          <> <button type="button" className="dash-software-open" onClick={onOpen}>Open Software Updates</button></>
        )}
      </span>
    </p>
  );
}

function Policy({ name, domain, policy, canEdit, busy, put }) {
  const confirm = useConfirm();
  const set = async (change) => {
    const next = { ...policy, ...change };
    if (policy.auto && !next.auto && !(await confirm({ ...W.ask.off(name), variant: "danger" }))) return;
    put(domain, next);
  };
  return (
    <header className="fw-card-head is-split">
      <div>
        <h3 className="fw-card-title">{name}</h3>
        <p className="fw-lead">{W.policy(policy)}</p>
      </div>
      {canEdit && (
        <div className="fw-ti-switches">
          <label>
            <button type="button" role="switch" aria-checked={!!policy.auto} aria-label={`${W.AUTO} for ${name}`} className="fw-ip-switch"
              disabled={busy} onClick={() => set({ auto: !policy.auto })}><span /></button> {W.AUTO}
          </label>
          {policy.auto && (
            <label>From
              <select className="fw-input" aria-label={`Update hour for ${name}`} value={policy.start} disabled={busy} onChange={(e) => set({ start: e.target.value })}>
                {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
    </header>
  );
}

function SiteRow({ site, canAct, busy, act }) {
  const n = W.next(site, canAct ? "from here" : "");
  const r = site.report;
  const held = r?.last?.result === "held" && r.last.serial > (r.serial || 0);
  const canUpdate = canAct && site.available > 0 && !site.ask?.serial && !["installing", "fetching"].includes(r?.state);
  return (
    <tr>
      <td>
        <strong>{site.node}</strong>
        {!site.online && <span className="fw-why"> Offline</span>}
      </td>
      <td data-label={W.cols.runs}>{W.runs(site)}</td>
      <td className="up-next" data-label={W.cols.next}>
        <span className={n.tone === "bad" ? "fw-why is-bad" : undefined}>{n.text}</span>
        {r?.restart_needed && <div className="fw-muted">{W.restartNeeded}</div>}
      </td>
      <td className="up-acts">
        {canUpdate && (held
          ? <button type="button" className="fw-btn" disabled={busy} onClick={() => act("force", site)}>Update anyway</button>
          : <button type="button" className="fw-btn" disabled={busy} onClick={() => act("now", site)}>Update now</button>)}
        {canAct && r?.restart_needed && <button type="button" className="fw-btn is-quiet" disabled={busy} onClick={() => act("restart", site)}>Restart</button>}
      </td>
    </tr>
  );
}

function Releases({ releases, isSuperAdmin, busy, act }) {
  if (!releases.length) return null;
  return (
    <section className="fw-card">
      <details className="up-releases">
        <summary className="fw-card-title">{W.RELEASES}</summary>
        <p className="fw-muted">{W.RELEASES_MEANS}</p>
        <div className="fw-log-wrap">
          <table className="fw-log up-sites cards-on-phone">
            <tbody>
              {releases.slice(0, 6).map((rel) => {
                const st = W.releaseState(rel);
                return (
                  <tr key={rel.serial}>
                    <td><strong>{W.version(rel.serial)}</strong><div className="fw-muted">{W.day(rel.published)}</div></td>
                    <td>{rel.notes}</td>
                    <td><span className={st.tone === "bad" ? "fw-why is-bad" : undefined}>{st.text}</span></td>
                    {isSuperAdmin && (
                      <td className="up-acts">
                        {rel.paused
                          ? <button type="button" className="fw-btn" disabled={busy} onClick={() => act("resume", rel)}>Resume</button>
                          : <button type="button" className="fw-btn is-danger-quiet" disabled={busy} onClick={() => act("pause", rel)}>Pause</button>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

export default function UpdatesPanel({ serverApiUrl }) {
  const session = useSession();
  const confirm = useConfirm();
  const { view, error, setError, read, mounted } = useUpdates(serverApiUrl);
  const [domains, setDomains] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchWithTimeout(serverApiUrl("/domains"), { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => mounted.current && d?.domains && setDomains(d.domains))
      .catch(() => {});
  }, [serverApiUrl]);

  // send makes one change, then reads the screen again.
  const send = async (method, path, body) => {
    setBusy(true);
    const res = await fetchWithTimeout(serverApiUrl(path), {
      method, credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    }, 30000).catch(() => null);
    const out = res ? await res.json().catch(() => ({})) : {};
    if (!mounted.current) return;
    setBusy(false);
    if (!res?.ok) {
      setError(out.error || "The Weavian server did not answer, so nothing changed.");
      return;
    }
    setError("");
    read();
  };

  const act = async (what, item) => {
    switch (what) {
      case "now":
        if (await confirm({ ...W.ask.now(item.node, item.available), variant: "primary" })) send("POST", "/updates/now", { node: item.node });
        break;
      case "force":
        if (await confirm({ ...W.ask.force(item.node), variant: "danger" })) send("POST", "/updates/now", { node: item.node, force: true });
        break;
      case "restart":
        if (await confirm({ ...W.ask.restart(item.node), variant: "danger" })) send("POST", "/updates/restart", { node: item.node });
        break;
      case "pause":
        if (await confirm({ ...W.ask.pause(item.serial), variant: "danger" })) send("POST", `/releases/${item.serial}/pause`, { why: "" });
        break;
      case "resume":
        send("POST", `/releases/${item.serial}/resume`, {});
        break;
      default:
    }
  };
  const put = (domain, policy) => send("PUT", "/updates/policy", { domain, ...policy });

  if (!view) return <div className="fw"><div className="fw-card"><p className="fw-muted">{error || "Reading…"}</p></div></div>;
  const names = Object.fromEntries(domains.map((d) => [d.id, d.name || d.id]));
  const groups = [...new Set(view.sites.map((s) => s.domain || ""))].sort((a, b) => (names[a] || a || "~").localeCompare(names[b] || b || "~"));
  const canAct = !!session.isTenantAdmin;
  return (
    <div className="fw">
      {error && <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{error}</span></div>}
      <section className="fw-card">
        <p className="fw-lead">{W.lead(view.sites)}</p>
      </section>
      {groups.map((d) => {
        const name = d ? names[d] || d : W.UNASSIGNED;
        return (
          <section className="fw-card" key={d || "-"}>
            <Policy name={name} domain={d} policy={view.policies[d] || view.default} canEdit={canAct} busy={busy} put={put} />
            <div className="fw-log-wrap">
              <table className="fw-log up-sites cards-on-phone">
                <thead><tr><th>{W.cols.site}</th><th>{W.cols.runs}</th><th>{W.cols.next}</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {view.sites.filter((s) => (s.domain || "") === d).map((s) => <SiteRow key={s.node} site={s} canAct={canAct} busy={busy} act={act} />)}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
      <Releases releases={view.releases} isSuperAdmin={!!session.isSuperAdmin} busy={busy} act={act} />
    </div>
  );
}
